import React, { ReactNode } from 'react';

import { useQuery } from '@apollo/client/react';
import { c } from 'ttag';

import { Button, LoaderPage, useAuthentication } from '@freelancelabs/components';
import { LogoFreelance } from '@freelancelabs/icons';
import { marquerSortie } from '@freelancelabs/shared';

import { RoleScope } from '../graphql/__generated__/schema';
import { ViewerDocument } from '../graphql/__generated__/types';

interface Props {
    children: ReactNode;
}

/**
 * Admission to the back-office, checked **once at the root** rather than route by
 * route: a per-route check is one someone forgets to add to the eleventh page.
 *
 * The discriminator is `role.scope`, which comes from our own database. It cannot
 * be an inside-api `appScope`: those are indexed by Cognito app client, and the
 * three front applications share the same one, so the portal and the back-office
 * carry the same scope and it separates nothing.
 *
 * Attention, this guard decides what is **displayed**, never what is readable. The API
 * refuses out-of-perimeter data on its own, through `membership_permission` and
 * the perimeter checked before the role. A guard in the browser is a courtesy to
 * the user, not a security boundary: the boundary is the API, plus the access
 * restriction on this host.
 */
const BackofficeGuard = ({ children }: Props) => {
    const { logout } = useAuthentication();
    const { data, loading, error } = useQuery(ViewerDocument);

    const memberships = data?.viewer?.memberships ?? [];
    const isStaff = memberships.some((membership) => membership?.role?.scope === RoleScope.Backoffice);

    /**
     * UN CLIENT QUI POUSSE LA PORTE DU BACK-OFFICE EST DECONNECTE ET RENVOYE A LA
     * CONNEXION, qui lui dit pourquoi. Decide par Mohamed le 2026-08-29, meme geste que
     * le portail pour qui n'a aucun rattachement.
     *
     * **CE N'EST QUE LA BRANCHE DU REFUS CONFIRME, et c'est tout l'interet de la
     * distinction que ce fichier documente au-dessus.** `error` ne deconnecte RIEN : on ne
     * sait pas encore, et mettre dehors sur une requete qui n'a pas repondu est
     * exactement l'accident deja constate ici, un administrateur en regle renvoye chez lui
     * parce que l'API locale etait arretee. Il garde donc son panneau, son « Recharger »
     * et le message du serveur en dessous.
     *
     * Un client refuse n'a, lui, rien a recharger : son compte ne changera pas de nature
     * en rechargeant la page. La sortie est la seule chose utile a lui offrir.
     */
    const refuse = !loading && !error && !isStaff;
    /**
     * SI LA SORTIE ECHOUE, ON RETOMBE SUR LE PANNEAU plutot que sur un tourniquet.
     *
     * `logout()` peut lever, Amplify parlant au reseau. Sans ce drapeau, l'ecran rendrait
     * `LoaderPage` pour toujours : une page qui n'a pas fini de charger et ne finira
     * jamais, ce qui est le pire des rendus possibles.
     *
     * On ne redirige PAS quand meme : la session est encore ouverte, donc `account`
     * renverrait la personne ici. Le panneau, lui, dit ce qui est vrai et porte la sortie.
     */
    const [sortieEnEchec, setSortieEnEchec] = React.useState(false);
    React.useEffect(() => {
        if (!refuse || sortieEnEchec) {
            return;
        }
        // On MARQUE puis on deconnecte, et on ne redirige PAS d'ici : `logout()` met
        // `hasSession` a faux, donc `Setup` part de toute facon vers `account`. Rediriger
        // ici aussi mettait deux navigations en concurrence, et c'est la seconde qui
        // gagnait.
        marquerSortie('APP_UNAUTHORIZED');
        void logout().catch(() => setSortieEnEchec(true));
    }, [refuse, sortieEnEchec, logout]);

    if (loading || (refuse && !sortieEnEchec)) {
        return <LoaderPage />;
    }

    /**
     * NE PAS SAVOIR N'EST PAS REFUSER, ET CE PANNEAU DIT LES DEUX SANS LES CONFONDRE.
     *
     * Le refus confirme part normalement par la sortie, plus haut. Il ne revient ici que
     * si cette sortie a ECHOUE, et il faut alors bien lui dire quelque chose. L'echec de
     * la requete, lui, passe toujours par ici : c'est le cas qui ne doit deconnecter
     * personne.
     *
     * L'echec ferme reste le bon choix, et son argument ne bouge pas : sur une zone
     * reservee, laisser entrer sur une erreur serait le mauvais sens. Ce qui etait faux
     * est la PHRASE. « Votre compte ne porte aucun rattachement interne » est une
     * affirmation sur les rattachements de la personne, et quand la requete a echoue on
     * n'en sait rien du tout.
     *
     * Constate en conditions reelles : l'API locale arretee, un administrateur en regle a
     * lu qu'il n'etait pas interne et a cherche du cote de son compte. Le serveur etait
     * eteint. Une phrase confiante sur un fait qu'on ignore coute plus cher qu'un aveu
     * d'ignorance.
     *
     * Le detail technique est garde sous le message, comme les vocabulaires de refus de
     * ce depot le font : c'est ce dont le support a besoin, et le paraphraser le rendrait
     * inutilisable.
     */
    if (error || refuse) {
        return (
            <main className="flex flex-1 h-full items-center justify-center flex-col gap-6 p-6">
                <LogoFreelance size={96} />
                <h1 className="text-xl font-semibold text-text text-center">
                    {error
                        ? c('Back-office').t`Your permissions could not be checked.`
                        : c('Back-office').t`This area is reserved for Freelance.com staff.`}
                </h1>
                <p className="text-text-600 text-center max-w-md">
                    {error
                        ? c('Back-office')
                              .t`This says nothing about your account: the query that reads your memberships did not answer. Reload the page, and tell your administrator if it persists.`
                        : c('Back-office')
                              .t`Your account does not carry an internal membership. If you think this is a mistake, contact your administrator.`}
                </p>
                {/* La reponse du serveur, telle quelle et en gris : elle ne s'adresse pas a
                    la personne, elle sert a celui qui va reparer. */}
                {error && <p className="text-xs text-text-hint text-center max-w-md break-words">{error.message}</p>}
                <div className="flex flex-wrap items-center justify-center gap-3">
                    {error && <Button onClick={() => window.location.reload()}>{c('Action').t`Reload`}</Button>}
                    <Button onClick={logout} category="weak">
                        {c('Action').t`Log out`}
                    </Button>
                </div>
            </main>
        );
    }

    return <>{children}</>;
};

export default BackofficeGuard;
